import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PixelAspectRatioCalculator = () => {
  const [inputData, setInputData] = useState({
    horizontalPixels: '',
    verticalPixels: '',
    displayAspectRatio: '16:9',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateAspectRatio = () => {
    const { horizontalPixels, verticalPixels, displayAspectRatio } = inputData;
    
    // Validate input
    if (!horizontalPixels || !verticalPixels) {
      setError('Please enter both horizontal and vertical pixels');
      return;
    }

    // Validate numeric input
    if (isNaN(horizontalPixels) || isNaN(verticalPixels)) {
      setError('Please enter valid numbers for pixels');
      return;
    }

    // Convert to numbers
    const numericHorizontal = parseInt(horizontalPixels);
    const numericVertical = parseInt(verticalPixels);

    // Parse display aspect ratio
    const [displayWidth, displayHeight] = displayAspectRatio.split(':').map(Number);
    const displayAspectRatioValue = displayWidth / displayHeight;

    // Calculate pixel aspect ratio
    const pixelAspectRatioValue = (numericHorizontal / numericVertical) / displayAspectRatioValue;
    const roundedPAR = parseFloat(pixelAspectRatioValue.toFixed(4));

    // Format result as ratio
    const result = `${roundedPAR}:1`;

    // Create visualization data
    const values = [numericHorizontal, numericVertical, pixelAspectRatioValue];
    const labels = ['Horizontal Pixels', 'Vertical Pixels', 'Pixel Aspect Ratio'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Values',
        data: values,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Calculate the ratio of horizontal to vertical pixels',
      `Horizontal/Vertical: ${numericHorizontal}/${numericVertical} = ${numericHorizontal/numericVertical}`,
      '\nStep 2: Divide by the display aspect ratio',
      `Pixel Aspect Ratio: (${numericHorizontal}/${numericVertical}) / (${displayWidth}/${displayHeight}) = ${roundedPAR}`,
      '\nStep 3: Format as a ratio',
      `Result: ${result}`
    ];

    setResults({
      horizontalPixels: numericHorizontal,
      verticalPixels: numericVertical,
      displayAspectRatio,
      result,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    horizontalPixels: '1920',
    verticalPixels: '1080',
    displayAspectRatio: '16:9',
    result: '1:1'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Pixel Aspect Ratio Calculator | Video and Image Tool</title>
          <meta name="description" content="Calculate the pixel aspect ratio for images and videos. This calculator helps determine the PAR based on resolution and display aspect ratio." />
          <meta name="keywords" content="pixel aspect ratio calculator, PAR calculator, video tools, image aspect ratio" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🖼️</div>
            <h1 className="text-3xl font-bold text-gray-800">Pixel Aspect Ratio Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the pixel aspect ratio (PAR) for images and videos. This calculator helps determine the PAR based on resolution and display aspect ratio.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Horizontal Pixels:</label>
              <input
                type="text"
                name="horizontalPixels"
                value={inputData.horizontalPixels}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter horizontal pixels (e.g., 1920)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Vertical Pixels:</label>
              <input
                type="text"
                name="verticalPixels"
                value={inputData.verticalPixels}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter vertical pixels (e.g., 1080)"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Aspect Ratio:</label>
              <select
                name="displayAspectRatio"
                value={inputData.displayAspectRatio}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="4:3">4:3 (Traditional TV)</option>
                <option value="16:9">16:9 (Widescreen)</option>
                <option value="21:9">21:9 (Ultra-Wide)</option>
                <option value="1:1">1:1 (Square)</option>
                <option value="2.39:1">2.39:1 (Cinematic)</option>
              </select>
            </div>
          </div>

          <button
            onClick={calculateAspectRatio}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Pixel Aspect Ratio
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Pixel Aspect Ratio Calculation</h3>
                  <p className="text-lg">Horizontal Pixels: {results.horizontalPixels}</p>
                  <p className="text-lg">Vertical Pixels: {results.verticalPixels}</p>
                  <p className="text-lg">Display Aspect Ratio: {results.displayAspectRatio}</p>
                  <p className="text-lg font-bold mt-4">Pixel Aspect Ratio: {results.result}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Aspect Ratio Formula: (Horizontal / Vertical) / (Display Aspect Ratio)</p>
                  <p className="text-lg">PAR Value: {parseFloat(results.result.split(':')[0])}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Values'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Parameters'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Pixel Aspect Ratio Components'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a common HD resolution:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Horizontal Pixels: {exampleData.horizontalPixels}</li>
                <li>Vertical Pixels: {exampleData.verticalPixels}</li>
                <li>Display Aspect Ratio: {exampleData.displayAspectRatio}</li>
                <li>Pixel Aspect Ratio: {exampleData.result}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the ratio of horizontal to vertical pixels
                  <ul className="list-disc pl-6">
                    <li>1920 / 1080 = 1.777...</li>
                  </ul>
                </li>
                <li>Divide by the display aspect ratio (16/9 = 1.777...)
                  <ul className="list-disc pl-6">
                    <li>1.777... / 1.777... = 1</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Pixel Aspect Ratio Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Resolution:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the horizontal and vertical pixel counts</li>
                </ul>
              </li>
              <li><strong>Select Display Aspect Ratio:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the appropriate display aspect ratio from the dropdown</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The pixel aspect ratio</li>
                  <li>The calculation details</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated pixel aspect ratio</li>
                  <li>Check the calculation steps</li>
                  <li>View the visual representation of values</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>A PAR of 1:1 means square pixels</li>
                <li>Different resolutions can have the same display aspect ratio but different PAR</li>
                <li>PAR is important for video formatting and display correctness</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Pixel Aspect Ratio <span className="text-blue-600">🖼️</span></h2>
          <div className="text-gray-600">
            <p>
              Pixel aspect ratio describes the proportional relationship between the width and height of individual pixels in a digital image or video. It's important because:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Display Accuracy:</strong> Ensures images and videos appear correctly on different displays</li>
              <li><strong>Video Production:</strong> Critical for maintaining correct geometry in video workflows</li>
              <li><strong>Resolution Compatibility:</strong> Helps maintain aspect ratio when scaling images</li>
              <li><strong>Historical Context:</strong> Older video standards often used non-square pixels</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your pixel values</li>
              <li><strong>Documentation:</strong> Record PAR information for video projects</li>
              <li><strong>Consistency:</strong> Maintain consistent PAR throughout video production</li>
              <li><strong>Display Knowledge:</strong> Understand your target display's aspect ratio</li>
            </ul>
            <p className="italic mt-4">
              Note: PAR is different from display aspect ratio. Always verify which one you need for your specific application.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PixelAspectRatioCalculator;